/**
 * familyPortal.js
 * ---------------
 * Pure view-model helpers for the Harbour family portal (SEN & ND Access
 * Portal, TDI-53, parent-or-carer side). They translate RMS referral data
 * into the states the family screens show: the Your children cards, the Your
 * child pathway cards, and the per-referral status pages. Statuses and labels
 * are the READY FOR DEV design vocabulary; the underlying signals are the
 * same ones the school portal reads (harbourPupils.js / referralStatus.js).
 */

import { STATUS, getTriageDecisionSummary } from './referralStatus';
import { pathwayReviews, pathwayTriage, pathwaysForConcern, PATHWAY_LABELS } from './triagePathways';
import { senStage, hasHfpReport, letterSharedWithRole } from './harbourPupils';

/**
 * The one provider the family portal serves today. Every family screen names
 * the service from here so they never disagree (the record's orgName is the
 * platform tenant, not the NHS service). Needs per-org config before a second
 * organisation is onboarded, as do the crisis numbers.
 */
export const FAMILY_SERVICE_NAME = 'Blackpool Teaching Hospitals';

/**
 * How schools reach the service about a referral it has received, as written
 * on the pupil record's sent state (4223:303920). Same per-org caveat as the
 * service name above.
 */
export const SERVICE_REFERRALS_CONTACT = { email: 'bfwh.ndp.referrals@nhs.net', phone: '01253 952503' };

/**
 * The Trust's Privacy Notice for its service users, which families read
 * before they consent. Same per-org caveat as the service name above.
 */
export const SERVICE_PRIVACY_NOTICE_URL = 'https://www.blackpoolteachinghospitals.nhs.uk/services/information-governance/confidentiality-and-our-privacy-notices/privacy-notice-our-service-users';

/**
 * The outcome letter embed URL for the SENCO flyout and the family letter
 * page: the signed PDF plus the PDF open-parameters fragment that hides the
 * browser viewer's toolbar and side panes, so service users see the letter
 * itself rather than a PDF application with print and download controls
 * (honoured by Chromium-based browsers; others degrade to their own minimal
 * viewer). The fragment never reaches the server, so the signed URL stays
 * valid. Saving a copy stays on the surfaces' own Download button, which goes
 * through the guarded attachment fetch.
 */
export function letterEmbedUrl(signedUrl) {
  if (!signedUrl) return signedUrl;
  return `${signedUrl}#toolbar=0&navpanes=0`;
}

/**
 * The guardian's own consent state for one pathway object (senPathway or
 * referralPathway): 'confirmed' | 'declined' | 'withdrawn' | 'needed'.
 * Records created before the family portal have no guardianConsent at all —
 * those read as 'needed' so the parent is asked once, on their first visit.
 */
export function guardianConsentState(pathwayData) {
  const decision = pathwayData && pathwayData.guardianConsent && pathwayData.guardianConsent.decision;
  if (decision === 'agree') return 'confirmed';
  if (decision === 'disagree') return 'declined';
  if (decision === 'withdraw') return 'withdrawn';
  return 'needed';
}

/**
 * The family's profile sharing answer on the referral (TDI-482), as the
 * referral view words it: 'Yes', 'No', or 'No (not answered)' when the
 * question was skipped at consent. Null when it was never asked (a referral
 * consented to before the question existed).
 */
export function profileSharingLabel(patient) {
  const sharing = patient && patient.referralPathway && patient.referralPathway.profileSharing;
  if (!sharing) return null;
  if (sharing.answer === 'yes') return 'Yes';
  return sharing.answered ? 'No' : 'No (not answered)';
}

/** Whether the referral is at a stage where the answer can still change. */
export function profileSharingChangeable(state) {
  return ['notStarted', 'inProgress', 'waitingForSchool', 'sentWaitingForReview', 'inReview'].includes(state);
}

/** Whether the referral pathway on this record has been stopped, and why. */
export function referralPathwayStop(patient) {
  const rp = (patient && patient.referralPathway) || {};
  if (rp.status === 'stopped') {
    return { stopped: true, reason: rp.stopReason || 'no_consent' };
  }
  return { stopped: false, reason: null };
}

/** Whether the SEND pathway on this record has been stopped, and why. */
export function senPathwayStop(patient) {
  const sen = (patient && patient.senPathway) || {};
  if (sen.status === 'stopped') {
    return { stopped: true, reason: sen.stopReason || 'no_consent' };
  }
  return { stopped: false, reason: null };
}

/** Map a stop reason onto the design's chip status. */
export function stopChipStatus(reason, pathway = 'sen') {
  if (reason !== 'consent_withdrawn') return 'stoppedNoConsent';
  // A withdrawn referral names who withdrew (TDI-474), because the school
  // reads the same chip and needs to know it was the family. The SEND
  // pathway keeps its own shorter words: the school is the other party
  // there, so there is nobody else it could have been.
  return pathway === 'referral'
    ? 'stoppedConsentWithdrawnByFamily'
    : 'stoppedConsentWithdrawn';
}

/**
 * Classify one referral pathway for the parent-or-carer view.
 *
 * The school classification (classifyPathwayForSchoolUser) plus the two
 * family-only states:
 * - stopped (no consent / consent withdrawn) wins over everything;
 * - waitingForSchool when the parent's forms are all in but the school's are
 *   not — the honest "nothing for you to do, the school is finishing" state.
 *
 * As on the school side, a settled decision reads as in review until the
 * outcome letter has been sent to the guardian role: a referral waiting on
 * letter sign-off or the admin-handover send has no outcome the parent can
 * see, and the view-letter action is hidden for the same reason.
 */
export function classifyPathwayForGuardian({
  reviews = [],
  triageStatus = null,
  readyForTriage = false,
  allFormsIn = false,
  guardianDone = false,
  schoolDone = false,
  stop = { stopped: false, reason: null },
  letterShared = false,
} = {}) {
  if (stop && stop.stopped) return stopChipStatus(stop.reason, 'referral');

  const triageComplete = String(triageStatus || '') === 'complete';
  const { outcome } = getTriageDecisionSummary(reviews, { triageComplete });
  if (outcome === 'accepted') return letterShared ? 'outcomeAvailable' : 'inReview';
  if (outcome === 'discharged') return letterShared ? 'closed' : 'inReview';

  const underway =
    triageStatus === 'in_triage' ||
    triageStatus === 'pending_second_review' ||
    (Array.isArray(reviews) && reviews.length > 0);
  if (underway) return 'inReview';

  if (readyForTriage || allFormsIn) return 'sentWaitingForReview';
  if (guardianDone && !schoolDone) return 'waitingForSchool';
  return 'inProgress';
}

/** Whether a referral pathway state means the referral has been sent in. */
export function isSentState(status) {
  return (
    status === 'sentWaitingForReview' ||
    status === 'inReview' ||
    status === 'outcomeAvailable' ||
    status === 'closed'
  );
}

/**
 * Build the per-concern referral rows for the family surfaces: one row per
 * triage pathway (ADHD, Autism, or both) with the guardian classification.
 */
export function buildFamilyReferralPathways(patient, {
  readyForTriage = false,
  allFormsIn = false,
  guardianDone = false,
  schoolDone = false,
} = {}) {
  if (!patient) return [];
  const stop = referralPathwayStop(patient);
  const keys = pathwaysForConcern(patient.intakeConcern);
  if (keys.length === 0) return [];
  return keys.map((key) => ({
    key,
    label: PATHWAY_LABELS[key],
    status: classifyPathwayForGuardian({
      reviews: pathwayReviews(patient, key),
      triageStatus: pathwayTriage(patient, key)?.status || null,
      readyForTriage,
      allFormsIn,
      guardianDone,
      schoolDone,
      stop,
      letterShared: letterSharedWithRole(patient, key, 'guardian'),
    }),
  }));
}

/** Map a role form-completion STATUS.* onto the family status-row chip. */
export function familyFormChip(roleStatus) {
  switch (roleStatus) {
    case STATUS.COMPLETED:
      return { status: 'done', label: 'Done' };
    case STATUS.STARTED:
      return { status: 'inProgress', label: 'In progress' };
    case STATUS.NOT_STARTED:
    case STATUS.INVITE_SENT:
    case STATUS.NOT_ASSIGNED:
    default:
      return { status: 'notStarted', label: 'Not started' };
  }
}

/**
 * The single overall chip for one pathway on a Your children card.
 * Reference: "Freddie's card on Your children in every status".
 */
export function familyCardChip(lane) {
  if (!lane) return null;
  if (lane.kind === 'sen') {
    if (lane.stopped) return { status: stopChipStatus(lane.stopReason), tone: 'neutral' };
    if (lane.reportAvailable) return { status: 'reportAvailable', tone: 'sen' };
    return { status: 'inProgress', tone: 'neutral' };
  }
  // Referral lane: the most settled pathway state leads, matching the
  // reference card (a single chip summarising the referral side).
  const order = [
    'outcomeAvailable', 'closed', 'stoppedConsentWithdrawnByFamily', 'stoppedNoConsent',
    'inReview', 'sentWaitingForReview', 'waitingForSchool', 'inProgress',
  ];
  const statuses = (lane.pathways || []).map((p) => p.status);
  const leading = order.find((s) => statuses.includes(s)) || 'inProgress';
  return { status: leading, tone: 'referral' };
}

/**
 * The referral lines on a Your children card (TDI-691). ADHD and autism move
 * independently (Tom, T-14 and T-18), so a child with both referrals gets a
 * line each, "ADHD" and "Autism", with its own chip in every status; a child
 * with one referral keeps the single "Referral pathway" line. `a11y` is how
 * the card's screen reader label names the line.
 */
export function familyCardReferralLines(lane) {
  if (!lane) return [];
  const pathways = lane.pathways || [];
  if (pathways.length < 2) {
    return [{ key: 'referral', label: 'Referral pathway', a11y: 'Referral pathway', chip: familyCardChip(lane) }];
  }
  return pathways.map((p) => ({
    key: p.key,
    label: p.label,
    a11y: `${p.label} referral`,
    chip: { status: p.status, tone: 'referral' },
  }));
}

/**
 * Build the full family view model for one child from the pupil view model
 * that useSencoPupils produces (it is role-agnostic: the rows are the records
 * this signed-in guardian is linked to).
 */
export function buildFamilyChild(pupil) {
  if (!pupil) return null;
  const patient = pupil.patient || {};
  const stage = senStage(patient);
  const senStopInfo = senPathwayStop(patient);
  const senActive = Boolean(stage);
  const senLane = senActive
    ? {
        kind: 'sen',
        stage: senStopInfo.stopped ? 'stopped' : stage,
        stopped: senStopInfo.stopped,
        stopReason: senStopInfo.reason,
        reportAvailable: hasHfpReport(patient),
        consent: guardianConsentState(patient.senPathway),
      }
    : null;

  const referralStarted = Boolean(
    patient.intakeConcern || (patient.referralPathway && patient.referralPathway.startedAt)
  );
  const guardianDone = pupil.guardianStatus === STATUS.COMPLETED;
  const schoolDone = pupil.schoolStatus === STATUS.COMPLETED;
  const readyForTriage = patient?.triageReadiness?.status === 'ready';
  const referralStopInfo = referralPathwayStop(patient);
  const referralLane = referralStarted
    ? {
        kind: 'referral',
        pathways: buildFamilyReferralPathways(patient, {
          readyForTriage,
          allFormsIn: readyForTriage,
          guardianDone,
          schoolDone,
        }),
        stopped: referralStopInfo.stopped,
        stopReason: referralStopInfo.reason,
        consent: guardianConsentState(patient.referralPathway),
        forLine: referralForLine(patient.intakeConcern),
      }
    : null;

  return {
    id: pupil.id,
    name: pupil.name,
    firstName: childFirstName(pupil),
    meta: [pupil.yearGroup, patient.school?.schoolName || patient.schoolName].filter(Boolean).join(' · '),
    sen: senLane,
    referral: referralLane,
  };
}

/**
 * The concern in the family's words: "ADHD", "autism" or "ADHD and autism".
 * Used wherever a sentence names the referral ("Freddie's ADHD referral").
 */
export function referralConcernWords(intakeConcern) {
  const keys = pathwaysForConcern(intakeConcern);
  if (keys.length === 0) return null;
  return keys.map((k) => (k === 'adhd' ? 'ADHD' : 'autism')).join(' and ');
}

// What the school referred the child for, told to the parent at the start of
// their Reason for Referral instead of asking them (R-023, TDI-631). Word for
// word from the ruling; keyed by referralPathway.referralFor.
const REFERRED_FOR_LINES = {
  adhd: 'Your child has been referred for ADHD.',
  asd: 'Your child has been referred for autism.',
  both: 'Your child has been referred for ADHD and autism.',
};

/**
 * The information line for the school's referral type, or null when the
 * record has no school-chosen type (a form-started referral).
 *
 * @param {string|null|undefined} referralFor - 'adhd' | 'asd' | 'both'
 * @returns {string|null}
 */
export function referredForLine(referralFor) {
  return REFERRED_FOR_LINES[referralFor] || null;
}

/** "For: ADHD and autism" line under the referral card title. */
export function referralForLine(intakeConcern) {
  const words = referralConcernWords(intakeConcern);
  return words ? `For: ${words}` : null;
}

/** The child's first name, for personalised strings. */
export function childFirstName(pupil) {
  const patient = (pupil && pupil.patient) || {};
  return (
    patient.subject_first_name ||
    patient.firstName ||
    (patient.subject && patient.subject.firstName) ||
    String((pupil && pupil.name) || '').split(/\s+/)[0] ||
    ''
  );
}

/**
 * Who at school still has forms to finish, in plain words (Jasmine, 6 Oct
 * 2026): "Priya Shah, SENCO at Test Primary School, still needs to complete
 * their forms." Falls back honestly when the name or school is not recorded.
 */
export function schoolStillToFinishLine(pupil) {
  const contact = (pupil && pupil.schoolContact) || {};
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
  const patient = (pupil && pupil.patient) || {};
  const school = (patient.school && patient.school.schoolName) || patient.schoolName || '';
  if (name && school) return `${name}, SENCO at ${school}, still needs to complete their forms.`;
  if (name) return `${name}, the SENCO, still needs to complete their forms.`;
  if (school) return `The SENCO at ${school} still needs to complete their forms.`;
  return 'The school still needs to complete their forms.';
}

/**
 * The signed-in parent's own name for the portal shell's menu button
 * (TDI-826 part 2). Parents sign in with an email link and the portal never
 * sets a display name, so the menu used to show their email address and a
 * one-letter monogram. Their name is already on each child's record, entered
 * by the school: the record where their account is linked wins, else a
 * guardian row carrying their email, else the account's display name, else
 * the email, as before.
 */
export function familyUserName(user, pupils) {
  if (!user) return '';
  const uid = user.uid || null;
  const email = String(user.email || '').trim().toLowerCase();
  let byEmail = null;
  for (const pupil of pupils || []) {
    for (const guardian of pupil.guardians || []) {
      const name = `${guardian.firstName || ''} ${guardian.lastName || ''}`.trim();
      if (!name) continue;
      if (uid && guardian.userId === uid) return name;
      if (!byEmail && email && String(guardian.email || '').trim().toLowerCase() === email) {
        byEmail = name;
      }
    }
  }
  return byEmail || user.displayName || user.email || '';
}
